<template>
    <HeadlessMenu as="div" :class="getRootClass()" v-slot="{ close }">
        <MenuCollapseBridge :collapsed="collapsed" :close-menu="close" />

        <MenuButton
            class="text-left transition"
            :class="[getMenuButtonClass(), getMenuButtonSurfaceClass()]"
            :aria-label="adminName"
            :title="collapsed ? adminName : undefined"
        >
            <span
                v-if="fixedRail"
                :class="getMenuButtonFrameClass()"
                aria-hidden="true"
            ></span>
            <span
                class="relative z-10 flex size-8 shrink-0 items-center justify-center rounded-full bg-indigo-100 text-xs font-bold text-indigo-700"
                :class="getAvatarClass()"
            >
                {{ adminInitials }}
            </span>
            <span
                v-if="fixedRail || !collapsed"
                :class="getUserLabelClass()"
                :aria-hidden="fixedRail && collapsed ? 'true' : undefined"
            >
                <span class="block truncate text-sm font-semibold text-slate-900">{{ adminName }}</span>
            </span>
            <ChevronUpIcon
                v-if="fixedRail || !collapsed"
                :class="getChevronClass()"
                aria-hidden="true"
            />
        </MenuButton>

        <transition
            v-if="!collapsed"
            enter-active-class="transition ease-out duration-100"
            enter-from-class="translate-y-1 transform opacity-0 scale-95"
            enter-to-class="translate-y-0 transform opacity-100 scale-100"
            leave-active-class="hidden"
        >
            <MenuItems
                :class="getMenuItemsClass()"
            >
                <MenuItem v-for="item in userNavigation" :key="item.name" v-slot="{ active }">
                    <RouterLink
                        :to="item.to"
                        :class="getDropdownItemClass(active, item.current)"
                    >
                        <component :is="item.icon" class="size-4 shrink-0 text-slate-400" aria-hidden="true" />
                        <span>{{ item.name }}</span>
                    </RouterLink>
                </MenuItem>

                <MenuItem key="logout" v-slot="{ active }">
                    <a
                        href="#"
                        @click.prevent="logout"
                        :class="getDropdownItemClass(active, false)"
                    >
                        <ArrowRightOnRectangleIcon class="size-4 shrink-0 text-slate-400" aria-hidden="true" />
                        <span>{{ t('common.logout') }}</span>
                    </a>
                </MenuItem>
            </MenuItems>
        </transition>
    </HeadlessMenu>
</template>

<script setup>
import { computed, defineComponent, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import VueCookies from 'vue-cookies'
import { Menu as HeadlessMenu, MenuButton, MenuItem, MenuItems } from '@headlessui/vue'
import { ArrowRightOnRectangleIcon, BuildingOffice2Icon, ChevronUpIcon, Cog6ToothIcon, CreditCardIcon, UserCircleIcon } from '@heroicons/vue/24/outline'
import { useAdminAuthStore } from '@/composables/useAdminAuthStore'
import { activeTenantModules, rememberTenantActiveModules, tenantHasModule } from '@/composables/useTenantModules.js'
import { useTenantAccess } from '@/composables/useTenantAccess.js'

const route = useRoute()
const router = useRouter()
const { t } = useI18n()
const adminAuthStore = useAdminAuthStore()
const { capabilities } = useTenantAccess()

const MenuCollapseBridge = defineComponent({
    props: {
        collapsed: {
            type: Boolean,
            default: false,
        },
        closeMenu: {
            type: Function,
            required: true,
        },
    },
    setup(props) {
        watch(() => props.collapsed, (value) => {
            if (value) {
                props.closeMenu()
            }
        }, { immediate: true, flush: 'sync' })

        return () => null
    },
})

const props = defineProps({
    collapsed: {
        type: Boolean,
        default: false,
    },
    fixedRail: {
        type: Boolean,
        default: false,
    },
})

const collapsed = computed(() => props.collapsed)
const fixedRail = computed(() => props.fixedRail)
const TAMEPULSE_SETTINGS_MODULES = [
    'approvals',
    'agents',
    'chat',
    'connections',
    'invoices',
    'knowledge_base',
    'mail',
    'messages',
    'playbooks',
    'skills',
    'templates',
]

const { result: tenantResult } = useQuery(gql`
    query AdminSidebarUserMenuTenant {
        currentTenant {
            id
            active_modules
        }
    }
`)

watch([() => route.params.tenantId, tenantResult], ([tenantId, value]) => {
    if (!value?.currentTenant) {
        return
    }

    rememberTenantActiveModules(tenantId, activeTenantModules(value.currentTenant))
}, { immediate: true })

const canManageBusinesses = computed(() => tenantHasModule(tenantResult.value?.currentTenant, 'quote'))
const canAccessTamepulseSettings = computed(() => {
    return TAMEPULSE_SETTINGS_MODULES.some((module) => tenantHasModule(tenantResult.value?.currentTenant, module))
})
const canAccessSettings = computed(() => {
    return canAccessTamepulseSettings.value
        || capabilities.value.includes('access.users.manage')
        || capabilities.value.includes('access.roles.manage')
})

const adminName = computed(() => adminAuthStore.admin?.name || adminAuthStore.admin?.email || t('common.account'))
const adminInitials = computed(() => {
    const value = adminAuthStore.admin?.name || adminAuthStore.admin?.email || 'A'
    const parts = String(value).trim().split(/\s+/).filter(Boolean)

    if (parts.length >= 2) {
        return `${parts[0][0]}${parts[1][0]}`.toUpperCase()
    }

    return String(value).slice(0, 2).toUpperCase()
})

const userNavigation = computed(() => [
    {
        name: t('common.profile'),
        to: { name: 'profile', params: { tenantId: route.params.tenantId } },
        icon: UserCircleIcon,
        current: route.name === 'profile',
    },
    ...(canManageBusinesses.value ? [{
        name: t('business.navigation.list'),
        to: { name: 'business.index', params: { tenantId: route.params.tenantId } },
        icon: BuildingOffice2Icon,
        current: route.name?.startsWith('business.'),
    }] : []),
    ...(canAccessSettings.value ? [{
        name: t('tamepulse.navigation.settings'),
        to: { name: 'tamepulse.settings.index', params: { tenantId: route.params.tenantId } },
        icon: Cog6ToothIcon,
        current: route.name?.startsWith('tamepulse.settings.') || route.name?.startsWith('tamepulse.environment.'),
    }] : []),
    {
        name: t('tamepulse.navigation.billing'),
        to: { name: 'settings.billing', params: { tenantId: route.params.tenantId } },
        icon: CreditCardIcon,
        current: route.name === 'settings.billing',
    },
    {
        name: t('tamepulse.navigation.offers'),
        to: { name: 'settings.billing.plans', params: { tenantId: route.params.tenantId } },
        icon: CreditCardIcon,
        current: route.name === 'settings.billing.plans',
    },
])

const getRootClass = () => [
    'relative',
    fixedRail.value
        ? (collapsed.value ? 'w-[3.25rem]' : 'w-full')
        : (collapsed.value ? 'w-11' : 'w-full'),
]

const getDropdownItemClass = (active, current) => [
    active || current ? 'bg-slate-50 outline-none' : '',
    current ? 'font-semibold text-indigo-700' : 'text-slate-900',
    'j-actions-menu-item flex w-full items-center justify-start gap-2 rounded-lg px-3 py-2 text-left text-sm/6 shadow-none transition',
]

const getMenuItemsClass = () => [
    'absolute bottom-full left-0 z-20 mb-1 origin-bottom rounded-lg border border-slate-200 bg-white px-2 py-2 shadow-xl ring-1 ring-slate-900/5 focus:outline-none',
    fixedRail.value ? 'w-[14.5rem]' : 'w-full',
]

const getMenuButtonClass = () => {
    if (fixedRail.value) {
        return [
            'group relative grid h-11 items-center overflow-hidden rounded-lg',
            collapsed.value
                ? 'mx-1 w-11 grid-cols-[2.75rem_minmax(0,0fr)_auto]'
                : 'w-full grid-cols-[3.25rem_minmax(0,1fr)_auto]',
        ]
    }

    return collapsed.value
        ? 'flex size-11 items-center justify-center p-0'
        : 'flex h-11 w-full items-center gap-2.5 px-2.5'
}

const getMenuButtonSurfaceClass = () => {
    if (fixedRail.value) {
        return ''
    }

    return 'rounded-lg border border-white/15 bg-white shadow-xl shadow-indigo-950/20 ring-1 ring-slate-900/5 hover:bg-slate-50'
}

const getMenuButtonFrameClass = () => {
    return [
        'pointer-events-none absolute inset-y-0 rounded-lg border border-white/15 bg-white shadow-xl shadow-indigo-950/20 ring-1 ring-slate-900/5 group-hover:bg-slate-50',
        collapsed.value ? 'inset-x-0' : 'left-1 right-0',
    ]
}

const getAvatarClass = () => {
    return fixedRail.value ? 'col-start-1 justify-self-center' : ''
}

const getUserLabelClass = () => {
    return [
        'min-w-0',
        fixedRail.value
            ? [
                'relative z-10 transition-[opacity,transform] duration-150 ease-out',
                collapsed.value ? 'pointer-events-none translate-x-1 opacity-0' : 'translate-x-0 opacity-100',
            ]
            : 'flex-1',
    ]
}

const getChevronClass = () => {
    return [
        'size-4 shrink-0 text-slate-400',
        fixedRail.value
            ? [
                'relative z-10 col-start-3 mr-2 transition-[opacity,transform] duration-150 ease-out',
                collapsed.value ? 'pointer-events-none translate-x-1 opacity-0' : 'translate-x-0 opacity-100',
            ]
            : '',
    ]
}

const logout = async () => {
    adminAuthStore.clearAdmin()
    VueCookies.remove('admin_token')

    await router.push({ name: 'login' })
}
</script>
